import { useCallback, useEffect, useRef, useState } from "react";
import { YIN } from "pitchfinder";
import { frequencyToMidi, frequencyToNoteName, type NoteName } from "../lib/guitarEngine";

// ─────────────────────────────────────────────────────────────
// useMonoPitchDetector — motor monofónico unificado (YIN)
//
// Diseño:
//   • Micrófono persistente: el AudioContext se mantiene vivo
//     mientras hay al menos un consumidor activo. Esto elimina
//     el overhead (~100-300 ms) de abrir/cerrar el mic en cada
//     iteración de detección.
//   • Pipeline robusto: AGC del navegador + highpass 70 Hz para
//     atenuar rumble + compressor para nivelar dinámica +
//     gain digital para señales suaves + lowpass 4 kHz que
//     limpia silbidos y armónicos altos antes de YIN.
//   • API dual:
//        subscribe(onNote, onStable)  → streaming continuo
//        detectNote({ targetMidi, … }) → Promise con la primera
//                                        nota estable que cumpla
//                                        los criterios, o null
//                                        si expira el timeout.
//   • Rechazo de octavas: median + comparación de cents +
//     desambiguación opcional contra un targetMidi conocido
//     (clave para canciones, donde sabemos qué nota toca).
//
// Reemplaza:
//   – usePitchDetector.tsx (queda como wrapper compat)
//   – useAudioEngine.detectNote (que usaba Basic Pitch, lento
//     y propenso a ±semitono/±octava en monofónica de guitarra)
// ─────────────────────────────────────────────────────────────

export interface MonoDetectedNote {
  note: NoteName;
  octave: number;
  midi: number;          // 0-127
  frequency: number;     // Hz
  cents: number;         // -50 … +50
  confidence: number;    // 0-1 (estabilidad de la mediana)
  volume: number;        // 0-1 RMS normalizado
}

export interface MonoPitchDetectorState {
  isReady: boolean;
  isListening: boolean;
  isLoading: boolean;
  error: string | null;
  volume: number;
  frequency: number;
  note: NoteName | null;
  octave: number;
  cents: number;
  midi: number;
  confidence: number;
  holdProgress: number;
}

export interface DetectNoteOptions {
  /** MIDI esperado: filtra ruido y valida la nota del pentagrama. */
  targetMidi?: number;
  /**
   * Acepta ±1 octava solo si YIN eligió mal la octava (|cents|>32), no si
   * la cuerda anterior sigue sonando una octava distinta. Default false.
   */
  acceptOctaveErrors?: boolean;
  /** Acepta error de ±1 semitono al comparar contra targetMidi (default false). */
  acceptSemitoneErrors?: boolean;
  /** Tiempo máximo de espera. Default 2000 ms. */
  timeoutMs?: number;
  /** Confianza mínima requerida (estabilidad de la mediana). Default 0.5. */
  minConfidence?: number;
  /** Tiempo de sostenimiento requerido para confirmar la nota. Default 150 ms. */
  holdTimeMs?: number;
  /** RMS mínimo para considerar audio significativo. Default 0.003. */
  threshold?: number;
  /**
   * Milisegundos de silencio (o muy bajo volumen) antes de empezar a contar
   * el hold. Evita que el sustain de la nota anterior valide la siguiente.
   */
  requireSilenceBeforeMs?: number;
  /** Desviación máxima en cents respecto al centro del semitono (default 45). */
  maxCents?: number;
}

export interface SubscribeOptions {
  /** Tiempo de sostenimiento para considerar la nota "estable". Default 120 ms. */
  holdTimeMs?: number;
  /** RMS mínimo para considerar audio significativo. Default 0.003. */
  threshold?: number;
  /**
   * Historial YIN más corto: menor latencia, ideal para corcheas/semicorcheas.
   */
  fastOnset?: boolean;
  /** Disparado en cada frame que hay una nota válida (no espera hold). */
  onNote?: (note: MonoDetectedNote) => void;
  /** Disparado una vez por nota una vez sostenida `holdTimeMs`. */
  onStable?: (note: MonoDetectedNote, durationMs: number) => void;
}

// ─── Constantes del DSP ───
const SAMPLE_RATE = 44100;
const BUFFER_SIZE = 4096;
const MIN_FREQ = 55;   // A1 ≈ 55 Hz (cuerda E2 = 82.4 Hz con margen)
const MAX_FREQ = 1500; // ≈ G6
const FREQ_HISTORY_SIZE = 12;
const SILENCE_RESET_FRAMES = 10;
const DEFAULT_THRESHOLD = 0.0015;
/** ~1.2 s de audio crudo para capturas polifónicas sin segundo micrófono. */
const RING_CAPACITY = 65536;

// Detector YIN único compartido (es puro – no guarda estado del input).
const detectYIN = YIN({
  sampleRate: SAMPLE_RATE,
  threshold: 0.12,
  probabilityThreshold: 0.05,
});

// ─── Helpers numéricos ───
function median(arr: number[]): number {
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function stdDev(arr: number[], mean: number): number {
  if (arr.length < 2) return Infinity;
  const variance = arr.reduce((s, v) => s + (v - mean) * (v - mean), 0) / arr.length;
  return Math.sqrt(variance);
}

/**
 * Desambiguación de octava sin sesgar hacia la nota esperada (evita que el
 * sustain de la nota anterior se "re-etiquete" como la siguiente).
 */
function resolveOctave(rawFreq: number): number {
  const candidates = [rawFreq, rawFreq / 2, rawFreq * 2].filter(
    (f) => f >= MIN_FREQ && f <= MAX_FREQ
  );
  if (candidates.length === 0) return rawFreq;

  let best = candidates[0];
  let bestCents = Math.abs(frequencyToNoteName(best).cents);
  for (let i = 1; i < candidates.length; i++) {
    const c = Math.abs(frequencyToNoteName(candidates[i]).cents);
    if (c < bestCents - 5) {
      best = candidates[i];
      bestCents = c;
    }
  }
  return best;
}

/**
 * Comparación nota esperada vs detectada. La tolerancia de octava solo aplica
 * si YIN parece haber elegido la octava equivocada (|cents| alto), no si la
 * cuerda sigue sonando limpia una octava arriba/abajo de la escrita.
 */
function matchesTargetMidi(
  detectedMidi: number,
  targetMidi: number,
  cents: number,
  opts: {
    acceptOctave: boolean;
    acceptSemitone: boolean;
    maxCents: number;
  }
): boolean {
  const d = Math.round(detectedMidi);
  const t = Math.round(targetMidi);
  const tol = opts.acceptSemitone ? 1 : 0;
  const absCents = Math.abs(cents);

  if (Math.abs(d - t) <= tol && absCents <= opts.maxCents) return true;

  if (!opts.acceptOctave) return false;
  if ((d % 12) !== (t % 12)) return false;
  if (Math.abs(Math.abs(d - t) - 12) > tol) return false;

  // Misma clase de altura, una octava de diferencia: solo corrección YIN
  return absCents > 32;
}

export interface MonoCaptureOptions {
  /** Entrada de línea / interfaz: sin AGC y ganancia más baja. */
  lineIn?: boolean;
  deviceId?: string | null;
}

// ─── Tipos internos del registro de subscribers ───
interface Subscriber {
  id: number;
  holdTimeMs: number;
  threshold: number;
  fastOnset: boolean;
  onNote?: (note: MonoDetectedNote) => void;
  onStable?: (note: MonoDetectedNote, durationMs: number) => void;
  candidate: { midi: number; frequency: number } | null;
  holdStart: number;
  lastReportedKey: string | null;
}

interface DetectRequest {
  id: number;
  targetMidi: number | undefined;
  acceptOctaveErrors: boolean;
  acceptSemitoneErrors: boolean;
  timeoutMs: number;
  minConfidence: number;
  holdTimeMs: number;
  threshold: number;
  maxCents: number;
  requireSilenceBeforeMs: number;
  /** false hasta cumplir requireSilenceBeforeMs (sustain de nota anterior). */
  armed: boolean;
  silenceAccumMs: number;
  lastTickAt: number;
  candidate: { midi: number; frequency: number } | null;
  holdStart: number;
  startedAt: number;
  resolve: (n: MonoDetectedNote | null) => void;
}

// ─── Hook ───
export function useMonoPitchDetector() {
  const [state, setState] = useState<MonoPitchDetectorState>({
    isReady: false,
    isListening: false,
    isLoading: false,
    error: null,
    volume: 0,
    frequency: 0,
    note: null,
    octave: 0,
    cents: 0,
    midi: 0,
    confidence: 0,
    holdProgress: 0,
  });

  // ─── Refs internas (estables a través de renders) ───
  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  // Tipar con ArrayBuffer explícito por TS 5.7+ (getFloatTimeDomainData
   // rechaza Float32Array<ArrayBufferLike>). El new Float32Array(N)
   // siempre crea un buffer concreto.
  const audioBufferRef = useRef<Float32Array<ArrayBuffer> | null>(null);
  const rafRef = useRef<number>(0);

  const subscribersRef = useRef<Map<number, Subscriber>>(new Map());
  const detectRequestsRef = useRef<Map<number, DetectRequest>>(new Map());
  const subIdSeqRef = useRef(0);
  const reqIdSeqRef = useRef(0);

  const freqHistoryRef = useRef<number[]>([]);
  const silenceFramesRef = useRef(0);
  const liveVolumeRef = useRef(0);
  const livePitchRef = useRef<{
    note: import("../lib/guitarEngine").NoteName | null;
    octave: number;
    midi: number;
    frequency: number;
  }>({ note: null, octave: 0, midi: 0, frequency: 0 });
  const ringBufferRef = useRef<Float32Array>(new Float32Array(RING_CAPACITY));
  const ringWriteRef = useRef(0);
  const captureOptionsRef = useRef<MonoCaptureOptions>({ lineIn: false });

  // ─── Lanzar / detener el loop. Función estable definida una sola vez. ───
  const startLoop = useCallback(() => {
    function evaluateRequestTimeouts(now: number) {
      detectRequestsRef.current.forEach((req, id) => {
        if (now - req.startedAt >= req.timeoutMs) {
          req.resolve(null);
          detectRequestsRef.current.delete(id);
        }
      });
    }

    function tick() {
      const analyser = analyserRef.current;
      const buf = audioBufferRef.current;
      if (!analyser || !buf) return;

      analyser.getFloatTimeDomainData(buf);

      const ring = ringBufferRef.current;
      let w = ringWriteRef.current;
      for (let i = 0; i < buf.length; i++) {
        ring[w] = buf[i];
        w = (w + 1) % RING_CAPACITY;
      }
      ringWriteRef.current = w;

      // RMS y volumen normalizado
      let sumSquares = 0;
      for (let i = 0; i < buf.length; i++) sumSquares += buf[i] * buf[i];
      const rms = Math.sqrt(sumSquares / buf.length);
      const volume = Math.min(rms * 30, 1);
      liveVolumeRef.current = volume;

      const now = performance.now();

      // ─── Silencio ───
      if (volume < DEFAULT_THRESHOLD) {
        livePitchRef.current = { note: null, octave: 0, midi: 0, frequency: 0 };
        silenceFramesRef.current++;
        if (silenceFramesRef.current > SILENCE_RESET_FRAMES) {
          freqHistoryRef.current = [];
          subscribersRef.current.forEach((s) => {
            s.candidate = null;
            s.holdStart = 0;
          });
          detectRequestsRef.current.forEach((r) => {
            r.candidate = null;
            r.holdStart = 0;
          });
        }

        setState((prev) => ({
          ...prev,
          volume,
          frequency: 0,
          note: null,
          cents: 0,
          midi: 0,
          confidence: 0,
          holdProgress: 0,
        }));

        evaluateRequestTimeouts(now);
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      silenceFramesRef.current = 0;

      // ─── Detección con YIN ───
      const rawPitch = detectYIN(buf);
      if (!rawPitch || rawPitch < MIN_FREQ || rawPitch > MAX_FREQ) {
        livePitchRef.current = { note: null, octave: 0, midi: 0, frequency: 0 };
        setState((prev) => ({
          ...prev,
          volume,
          frequency: 0,
          note: null,
          cents: 0,
          midi: 0,
          confidence: 0,
          holdProgress: 0,
        }));
        evaluateRequestTimeouts(now);
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      // Suavizado por mediana (historial corto si algún subscriber pide fastOnset)
      let useFastOnset = false;
      subscribersRef.current.forEach((s) => {
        if (s.fastOnset) useFastOnset = true;
      });
      const histCap = useFastOnset ? 6 : FREQ_HISTORY_SIZE;
      const minFramesForConfidence = useFastOnset ? 3 : 6;

      const hist = freqHistoryRef.current;
      hist.push(rawPitch);
      if (hist.length > histCap) hist.shift();

      let smoothed = rawPitch;
      let confidence = 0;
      if (hist.length >= minFramesForConfidence) {
        smoothed = median(hist);
        const dev = stdDev(hist, smoothed);
        confidence = Math.max(0, 1 - dev / smoothed);
      } else {
        confidence = Math.min(0.45, hist.length / histCap);
      }

      // ─── Estado UI: usar resolución sin target ───
      const resolvedUi = resolveOctave(smoothed);
      const midiUi = frequencyToMidi(resolvedUi);
      const infoUi = frequencyToNoteName(resolvedUi);
      const noteKey = `${infoUi.note}-${infoUi.octave}`;
      livePitchRef.current = {
        note: infoUi.note,
        octave: infoUi.octave,
        midi: midiUi,
        frequency: resolvedUi,
      };

      // ─── Subscribers: hold por nota (sin target) ───
      let maxHoldProgress = 0;
      subscribersRef.current.forEach((sub) => {
        if (volume < sub.threshold) return;

        if (!sub.candidate || Math.round(sub.candidate.midi) !== Math.round(midiUi)) {
          sub.candidate = { midi: midiUi, frequency: resolvedUi };
          sub.holdStart = now;
          sub.lastReportedKey = null;
        }

        const elapsed = now - sub.holdStart;
        const holdTarget = sub.holdTimeMs > 0 ? sub.holdTimeMs : 1;
        const progress = Math.min(elapsed / holdTarget, 1);
        maxHoldProgress = Math.max(maxHoldProgress, progress);

        const detected: MonoDetectedNote = {
          note: infoUi.note,
          octave: infoUi.octave,
          midi: midiUi,
          frequency: resolvedUi,
          cents: infoUi.cents,
          confidence,
          volume,
        };

        sub.onNote?.(detected);

        const reached = sub.holdTimeMs === 0 || elapsed >= sub.holdTimeMs;
        if (reached && sub.lastReportedKey !== noteKey) {
          sub.lastReportedKey = noteKey;
          sub.onStable?.(detected, elapsed);
        }
      });

      // ─── Requests detectNote() ───
      detectRequestsRef.current.forEach((req, id) => {
        const frameMs = Math.min(50, Math.max(8, now - req.lastTickAt));
        req.lastTickAt = now;

        const isQuiet = volume < req.threshold;

        // Fase 1: esperar silencio tras la nota anterior (sustain)
        if (!req.armed) {
          if (isQuiet) {
            req.silenceAccumMs += frameMs;
            if (req.silenceAccumMs >= req.requireSilenceBeforeMs) {
              req.armed = true;
              req.candidate = null;
              req.holdStart = 0;
              freqHistoryRef.current = [];
            }
          } else {
            req.silenceAccumMs = 0;
          }
          return;
        }

        if (isQuiet) {
          req.candidate = null;
          req.holdStart = 0;
          return;
        }

        const reqFreq = resolveOctave(smoothed);
        const reqMidi = frequencyToMidi(reqFreq);
        const reqInfo = frequencyToNoteName(reqFreq);

        if (typeof req.targetMidi === "number") {
          const ok = matchesTargetMidi(reqMidi, req.targetMidi, reqInfo.cents, {
            acceptOctave: req.acceptOctaveErrors,
            acceptSemitone: req.acceptSemitoneErrors,
            maxCents: req.maxCents,
          });
          if (!ok) {
            req.candidate = null;
            req.holdStart = 0;
            return;
          }
        }

        if (
          !req.candidate ||
          Math.round(req.candidate.midi) !== Math.round(reqMidi)
        ) {
          req.candidate = { midi: reqMidi, frequency: reqFreq };
          req.holdStart = now;
          return;
        }

        const elapsed = now - req.holdStart;
        if (elapsed >= req.holdTimeMs && confidence >= req.minConfidence) {
          const detected: MonoDetectedNote = {
            note: reqInfo.note,
            octave: reqInfo.octave,
            midi: reqMidi,
            frequency: reqFreq,
            cents: reqInfo.cents,
            confidence,
            volume,
          };
          req.resolve(detected);
          detectRequestsRef.current.delete(id);
        }
      });

      // Publicar estado UI (volume + nota + holdProgress)
      setState((prev) => ({
        ...prev,
        volume,
        frequency: resolvedUi,
        note: infoUi.note,
        octave: infoUi.octave,
        cents: infoUi.cents,
        midi: midiUi,
        confidence,
        holdProgress: subscribersRef.current.size > 0
          ? maxHoldProgress
          : prev.holdProgress,
      }));

      evaluateRequestTimeouts(now);
      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
  }, []);

  // ─── Inicio (idempotente) ───
  const start = useCallback(async (): Promise<void> => {
    if (audioContextRef.current) return;

    setState((s) => ({ ...s, isLoading: true, error: null }));

    try {
      const cap = captureOptionsRef.current;
      const preferred: MediaTrackConstraints = {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: !cap.lineIn,
      };
      if (cap.deviceId) {
        preferred.deviceId = { ideal: cap.deviceId };
      }

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: preferred });
      } catch (firstErr) {
        // Algunos navegadores/dispositivos rechazan constraints estrictos (OverconstrainedError).
        const errName =
          firstErr && typeof firstErr === "object" && "name" in firstErr
            ? String((firstErr as { name: string }).name)
            : "";
        if (errName === "OverconstrainedError" || errName === "NotFoundError") {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } else {
          throw firstErr;
        }
      }
      streamRef.current = stream;

      let ctx: AudioContext;
      try {
        ctx = new AudioContext({ sampleRate: SAMPLE_RATE });
      } catch {
        ctx = new AudioContext();
      }
      if (ctx.state === "suspended") await ctx.resume();
      audioContextRef.current = ctx;

      const source = ctx.createMediaStreamSource(stream);

      const highpass = ctx.createBiquadFilter();
      highpass.type = "highpass";
      highpass.frequency.value = 70;

      const compressor = ctx.createDynamicsCompressor();
      compressor.threshold.value = -50;
      compressor.knee.value = 40;
      compressor.ratio.value = 12;
      compressor.attack.value = 0.003;
      compressor.release.value = 0.1;

      const gain = ctx.createGain();
      gain.gain.value = cap.lineIn ? 1.75 : 2.5;

      const lowpass = ctx.createBiquadFilter();
      lowpass.type = "lowpass";
      lowpass.frequency.value = 4000;

      const analyser = ctx.createAnalyser();
      analyser.fftSize = BUFFER_SIZE;
      analyser.smoothingTimeConstant = 0;

      source
        .connect(highpass)
        .connect(compressor)
        .connect(gain)
        .connect(lowpass)
        .connect(analyser);

      analyserRef.current = analyser;
      audioBufferRef.current = new Float32Array(BUFFER_SIZE);
      freqHistoryRef.current = [];
      silenceFramesRef.current = 0;
      ringWriteRef.current = 0;
      ringBufferRef.current.fill(0);

      setState((s) => ({
        ...s,
        isReady: true,
        isLoading: false,
        isListening: true,
      }));

      startLoop();
    } catch (err) {
      console.error("[MonoPitchDetector] mic error:", err);
      setState((s) => ({
        ...s,
        isLoading: false,
        isReady: false,
        isListening: false,
        error: "No se pudo acceder al micrófono.",
      }));
      // No re-lanzar: el caller ya ve el error en state.error
    }
  }, [startLoop]);

  // ─── Cierre completo ───
  const stop = useCallback((): void => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    analyserRef.current = null;
    audioBufferRef.current = null;
    subscribersRef.current.clear();
    detectRequestsRef.current.forEach((r) => r.resolve(null));
    detectRequestsRef.current.clear();
    freqHistoryRef.current = [];
    silenceFramesRef.current = 0;

    setState({
      isReady: false,
      isListening: false,
      isLoading: false,
      error: null,
      volume: 0,
      frequency: 0,
      note: null,
      octave: 0,
      cents: 0,
      midi: 0,
      confidence: 0,
      holdProgress: 0,
    });
  }, []);

  const configureCapture = useCallback(
    (opts: MonoCaptureOptions) => {
      captureOptionsRef.current = { ...captureOptionsRef.current, ...opts };
      if (audioContextRef.current) {
        stop();
      }
    },
    [stop]
  );

  // ─── API: subscribe (streaming) ───
  const subscribe = useCallback((opts: SubscribeOptions = {}): (() => void) => {
    const id = ++subIdSeqRef.current;
    subscribersRef.current.set(id, {
      id,
      holdTimeMs: opts.holdTimeMs ?? 120,
      threshold: opts.threshold ?? DEFAULT_THRESHOLD,
      fastOnset: opts.fastOnset ?? false,
      onNote: opts.onNote,
      onStable: opts.onStable,
      candidate: null,
      holdStart: 0,
      lastReportedKey: null,
    });
    return () => {
      subscribersRef.current.delete(id);
    };
  }, []);

  // ─── API: detectNote (request/response) ───
  const detectNote = useCallback(
    async (opts: DetectNoteOptions = {}): Promise<MonoDetectedNote | null> => {
      if (!audioContextRef.current) {
        try {
          await start();
        } catch {
          return null;
        }
      }

      const requireSilence = opts.requireSilenceBeforeMs ?? 0;
      freqHistoryRef.current = [];

      const id = ++reqIdSeqRef.current;
      const now = performance.now();
      return new Promise<MonoDetectedNote | null>((resolve) => {
        detectRequestsRef.current.set(id, {
          id,
          targetMidi: opts.targetMidi,
          acceptOctaveErrors: opts.acceptOctaveErrors ?? false,
          acceptSemitoneErrors: opts.acceptSemitoneErrors ?? false,
          timeoutMs: opts.timeoutMs ?? 2000,
          minConfidence: opts.minConfidence ?? 0.5,
          holdTimeMs: opts.holdTimeMs ?? 150,
          threshold: opts.threshold ?? DEFAULT_THRESHOLD,
          maxCents: opts.maxCents ?? 45,
          requireSilenceBeforeMs: requireSilence,
          armed: requireSilence <= 0,
          silenceAccumMs: 0,
          lastTickAt: now,
          candidate: null,
          holdStart: 0,
          startedAt: now,
          resolve,
        });
      });
    },
    [start]
  );

  // ─── Cancelar todas las requests pendientes (sin cerrar el mic) ───
  const cancelPendingRequests = useCallback(() => {
    detectRequestsRef.current.forEach((r) => r.resolve(null));
    detectRequestsRef.current.clear();
  }, []);

  const getRecentSamples = useCallback((durationMs: number): { samples: Float32Array; sampleRate: number } => {
    const ctx = audioContextRef.current;
    const sampleRate = ctx?.sampleRate ?? SAMPLE_RATE;
    const count = Math.min(
      RING_CAPACITY,
      Math.max(1, Math.floor((sampleRate * durationMs) / 1000))
    );
    const out = new Float32Array(count);
    const ring = ringBufferRef.current;
    let w = ringWriteRef.current;
    for (let i = count - 1; i >= 0; i--) {
      w = (w - 1 + RING_CAPACITY) % RING_CAPACITY;
      out[i] = ring[w];
    }
    return { samples: out, sampleRate };
  }, []);

  const resetPitchHistory = useCallback(() => {
    freqHistoryRef.current = [];
    silenceFramesRef.current = 0;
    subscribersRef.current.forEach((s) => {
      s.candidate = null;
      s.holdStart = 0;
      s.lastReportedKey = null;
    });
  }, []);

  /**
   * Espera a que el volumen baje (cuerda enmudecida) antes de escuchar la
   * siguiente nota. Útil justo después de advance().
   */
  const waitForSilence = useCallback(
    async (opts?: {
      minMs?: number;
      timeoutMs?: number;
      threshold?: number;
    }): Promise<boolean> => {
      if (!audioContextRef.current) {
        try {
          await start();
        } catch {
          return false;
        }
      }

      const minMs = opts?.minMs ?? 120;
      const timeoutMs = opts?.timeoutMs ?? 500;
      const threshold = opts?.threshold ?? DEFAULT_THRESHOLD * 0.85;
      const deadline = performance.now() + timeoutMs;
      let silentMs = 0;

      while (performance.now() < deadline) {
        await new Promise<void>((r) => requestAnimationFrame(() => r()));
        if (liveVolumeRef.current < threshold) {
          silentMs += 16;
          if (silentMs >= minMs) {
            resetPitchHistory();
            return true;
          }
        } else {
          silentMs = 0;
        }
      }
      resetPitchHistory();
      return false;
    },
    [start, resetPitchHistory]
  );

  // Cleanup automático al desmontar
  useEffect(() => {
    return () => stop();
  }, [stop]);

  const getLiveVolume = useCallback((): number => liveVolumeRef.current, []);
  const getLivePitch = useCallback(
    () => ({ ...livePitchRef.current }),
    []
  );

  return {
    state,
    start,
    stop,
    configureCapture,
    subscribe,
    detectNote,
    cancelPendingRequests,
    resetPitchHistory,
    getRecentSamples,
    waitForSilence,
    /** Volumen RMS del último frame de audio (sin esperar re-render de React). */
    getLiveVolume,
    /** Pitch del último frame válido (note/octave/midi). */
    getLivePitch,
  };
}
